import { Fragment, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import styled from "@emotion/styled";
import Fields from "@components/fields";
import Button from "@components/button";
import generalSettings from "@apis/generalSettings";
import { toast } from "react-toastify";
import CardHeader from "@commons/businesscardHeader";
import Typography from "@components/typography";
import Wrapper from "@components/wrapper";
import useIsMobile from "@hooks/useIsMobile";

const AppSettings = () => {
  const isMobile = useIsMobile(800);
  const [existingData, setExistingData] = useState({
    user: null,
    store: null,
    delivery: null,
  });

  const appTypes = [
    { key: "user", label: "User", state: "appSettings" },
    { key: "store", label: "Store", state: "appSettingsStore" },
    { key: "delivery", label: "Delivery", state: "appSettingsDelivery" },
  ];

  const fetchAppSettings = async () => {
    try {
      const dataPromises = appTypes.map(async (appType) => {
        try {
          const data = await generalSettings.getByKey({
            id: `app-version-${appType.key}`,
            state: appType.state,
            showErr: false,
          });
          return { key: appType.key, data };
        } catch (err) {
          console.error(`Failed to fetch ${appType.key} app settings`, err);
          return { key: appType.key, data: null };
        }
      });

      const results = await Promise.all(dataPromises);
      const newData: any = {};
      results.forEach((result) => {
        newData[result.key] = result.data;
      });
      setExistingData(newData);
    } catch (err) {
      console.error("Failed to fetch app version settings", err);
    }
  };

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      const promises = appTypes.map(async (appType) => {
        const appValues = {
          androidVersion: values[`${appType.key}AndroidVersion`] || "",
          iosVersion: values[`${appType.key}IosVersion`] || "",
          playStoreLink: values[`${appType.key}PlayStoreLink`] || "",
          appStoreLink: values[`${appType.key}AppStoreLink`] || "",
        };

        const payload: any = {
          key: `app-version-${appType.key}`,
          title: `App Version ${appType.label}`,
          infos: appValues,
        };

        const existing = existingData[appType.key];

        if (existing?.id) {
          return generalSettings.update({
            id: existing.id,
            state: appType.state,
            data: payload,
          });
        } else {
          return generalSettings.create({
            state: appType.state,
            data: payload,
          });
        }
      });

      await Promise.all(promises);
      toast.success("All app settings saved successfully!");
      fetchAppSettings();
    } catch (err) {
      console.error("Failed to save app settings", err);
      toast.error("Failed to save app settings");
    }
  };

  const getInitialValues = () => {
    const initialValues = {};

    appTypes.forEach((appType) => {
      const existing = existingData[appType.key];
      const infos = existing?.infos || {};

      initialValues[`${appType.key}AndroidVersion`] =
        infos.androidVersion || "";
      initialValues[`${appType.key}IosVersion`] = infos.iosVersion || "";
      initialValues[`${appType.key}PlayStoreLink`] = infos.playStoreLink || "";
      initialValues[`${appType.key}AppStoreLink`] = infos.appStoreLink || "";
    });

    return initialValues;
  };

  return (
    <Fragment>
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
      >
        <Form>
          <CardHeader title="App Download Information" />
          <Wrapper>
            {appTypes.map((appType) => (
              <div key={appType.key}>
                <Typography size={16} weight={600}>
                  {appType.label}
                </Typography>
                <br />
                <ResponsiveGrid>
                  {/* Android Section */}
                  <Section>
                    <Typography size={18} weight={500}>
                      For Android
                    </Typography>
                    <Fields.Input
                      name={`${appType.key}AndroidVersion`}
                      label="Minimum App Version for Force Update"
                      placeholder="Enter Version"
                      styleInput="width:100%"
                    />
                    <Fields.Input
                      name={`${appType.key}PlayStoreLink`}
                      label="Download URL for App"
                      placeholder="Enter App Url"
                      styleInput="width:100%"
                    />
                  </Section>

                  {/* iOS Section */}
                  <Section>
                    <Typography size={18} weight={500}>
                      For iOS
                    </Typography>
                    <Fields.Input
                      name={`${appType.key}IosVersion`}
                      label="Minimum App Version for Force Update"
                      placeholder="Enter Version"
                      styleInput="width:100%"
                    />
                    <Fields.Input
                      name={`${appType.key}AppStoreLink`}
                      label="Download URL for App"
                      placeholder="Enter App Url"
                      styleInput="width:100%"
                    />
                  </Section>
                </ResponsiveGrid>
              </div>
            ))}

            <br />
            <Button type="submit" title="Save All Settings" justify="end" />
          </Wrapper>
        </Form>
      </Formik>
    </Fragment>
  );
};

export default AppSettings;

// ---------------- Styled ----------------
const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  border: 1px solid #f1f1f1;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;
